import { Button, Col, Row, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import {
  ANCHOR_EDGE,
  ANCHOR_KIND,
  WAVE_PRESETS,
  anchorFromDraft,
  anchorToDraft,
  emptyAnchorDraft,
  matchPreset,
  presetByKey,
  validateAnchorPair,
  waveEdgesLabel
} from '../anchors';
import { REWARDS_SOURCE, WAVE_STATUS, isTerminalStatus, resolveTapTargetBounds, validateTapTarget } from '../constants';
import { describeConflict, orderingError, resolveAnchorLocally, slotBounds } from '../timeline';
import { toDatetimeLocal } from '../waveEditPayload';
import { LIVE_CLOSE_KINDS, buildAnchorUpdatePayload } from '../waveAnchorPayload';
import AnchorPicker from './AnchorPicker';
import RewardsFields, {
  normalizeRewards,
  toEditableRewards,
  validateRewards
} from './RewardsFields';
import { Badge, STATUS_BADGE_VARIANT } from './ui';
import { FieldError, FieldHint, Notice, Select, fmtDateTime, fmtMaybe } from './timelineUi';

// One wave beneath the track: the summary line the track segment echoes, expanding into the two
// EDGE PICKERS plus target and rewards.
//
// Three status rules from the API shape the whole row, and each is stated on screen rather than
// discovered through an error:
//   active    — the start cannot change (participation is anchored to it), and the API refuses an
//               anchor PAIR outright on a live wave, so the close travels on its legacy field.
//   terminal  — an edit answers 409. Delete is offered, not Save.
//   scheduled — everything is editable, and both anchors are re-sent together.

const seedRewards = (configRewards, row) => {
  if (!row?.rewards) return toEditableRewards(configRewards);
  // A scheduled wave's `rewards` is the partial override it was created with, so it has to be
  // layered back over the channel defaults to show the effective rules; a live wave's is already
  // the fully-resolved snapshot.
  if (row.rewardsSource === REWARDS_SOURCE.OVERRIDE) {
    return toEditableRewards({ ...(configRewards || {}), ...row.rewards });
  }
  return toEditableRewards(row.rewards);
};

const WaveEditorRow = ({
  wave,
  // The matching GET /list row, when it has loaded — it carries the wave's rewards and the legacy
  // schedule fields the timeline endpoint does not report.
  listRow,
  anchors,
  timeline,
  config,
  index,
  expanded,
  onToggle,
  // A handle dragged on the track, handed down rather than applied by the parent: the row owns
  // its drafts, and a dragged edge is an edit in progress, not a saved change.
  pendingEdge,
  onSaved,
  onRequestDelete,
  onRequestCancel
}) => {
  const status = wave.status;
  const isLive = status === WAVE_STATUS.ACTIVE;
  const terminal = isTerminalStatus(status);
  const conflicts = timeline.conflictsByWave.get(wave.waveId) || [];

  const [openDraft, setOpenDraft] = useState(() => emptyAnchorDraft());
  const [closeDraft, setCloseDraft] = useState(() => emptyAnchorDraft());
  const [target, setTarget] = useState('');
  const [rewards, setRewards] = useState(() => toEditableRewards(null));
  const [original, setOriginal] = useState(null);
  const [presetKey, setPresetKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [saved, setSaved] = useState(false);

  // Seed the editor from the wave. Re-seeds when the row's anchors change identity, which is what
  // makes a post-mutation refetch land in the fields instead of being overwritten by stale state.
  const anchorKey = JSON.stringify([anchors?.open, anchors?.close]);
  useEffect(() => {
    if (!expanded) return;
    const open = anchorToDraft(anchors?.open, toDatetimeLocal);
    const close = anchorToDraft(anchors?.close, toDatetimeLocal);
    const seededRewards = seedRewards(config?.rewards, listRow);
    setOpenDraft(open);
    setCloseDraft(close);
    setTarget(wave.tapTarget == null ? '' : String(wave.tapTarget));
    setRewards(seededRewards);
    setOriginal({
      open: anchors?.open ? { ...anchors.open } : null,
      close: anchors?.close ? { ...anchors.close } : null,
      target: wave.tapTarget,
      rewards: normalizeRewards(seededRewards)
    });
    setServerError(null);
    setSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, anchorKey, wave.waveId, wave.tapTarget, listRow, config]);

  // Apply a dragged edge. The nonce is the dependency, so dragging to the same place twice still
  // registers, and the seeding effect above cannot overwrite it — that one keys on the SAVED
  // anchors, which a drag has not changed.
  useEffect(() => {
    if (!pendingEdge?.anchor) return;
    const next = anchorToDraft(pendingEdge.anchor, toDatetimeLocal);
    if (pendingEdge.edge === ANCHOR_EDGE.OPEN) {
      // A running wave's start is immutable, so a drag on it is refused here rather than silently
      // building a body the API rejects.
      if (!isLive) setOpenDraft(next);
      return;
    }
    // A live wave can only close on a date-time or a match minute; a drag that snapped to an
    // event has no legacy field to travel on, so it is not applied.
    if (isLive && !LIVE_CLOSE_KINDS.includes(next.kind)) return;
    setCloseDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEdge?.nonce]);

  const bounds = useMemo(() => resolveTapTargetBounds(config), [config]);
  const effectiveTarget = target === '' ? config?.tapTarget ?? config?.resolvedTarget : Number(target);

  // Which preset the current pair IS — shown as the dropdown's selection so a hand-built schedule
  // reads as "Custom" rather than silently claiming the last preset picked.
  const currentPreset = useMemo(() => {
    if (!original) return null;
    const asAnchor = (d) =>
      d.kind === ANCHOR_KIND.MATCH_EVENT
        ? { kind: d.kind, event: d.event, offsetMin: Number(d.offsetMin) || 0 }
        : d.kind === ANCHOR_KIND.MATCH_MINUTE
          ? { kind: d.kind, minute: Number(d.minute) }
          : d.kind === ANCHOR_KIND.ABSOLUTE
            ? { kind: d.kind, at: d.at }
            : {
                kind: d.kind,
                durationMin: Number(d.durationMin),
                untilEvent: d.untilEvent || undefined
              };
    return matchPreset(asAnchor(openDraft), asAnchor(closeDraft), {
      postFtMinutes: timeline.postFtMinutes
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDraft, closeDraft, timeline.postFtMinutes, original]);

  const applyPreset = (key) => {
    setPresetKey(key);
    const preset = presetByKey(key);
    if (!preset) return;
    const seeded = preset.seed({
      postFtMinutes: timeline.postFtMinutes,
      // The presets that end (or start) on the wall clock keep whatever the admin already typed,
      // so picking one never wipes a date they had entered.
      at: closeDraft.at || openDraft.at,
      openAt: openDraft.at,
      open: preset.keepsOpen
        ? {
            kind: openDraft.kind,
            event: openDraft.event,
            offsetMin: Number(openDraft.offsetMin) || 0,
            minute: Number(openDraft.minute),
            at: openDraft.at
          }
        : undefined
    });
    if (!preset.keepsOpen) setOpenDraft(anchorToDraft(seeded.open, toDatetimeLocal));
    setCloseDraft(anchorToDraft(seeded.close, toDatetimeLocal));
  };

  const validation = validateAnchorPair(openDraft, closeDraft);
  const orderErr = validation.pair
    ? null
    : orderingError(
        anchorFromDraft(openDraft),
        anchorFromDraft(closeDraft),
        { phaseByEvent: timeline.phaseByEvent, postFtMinutes: timeline.postFtMinutes }
      );
  // The free window this wave may move within — the times its SIBLINGS hold, greyed out in the
  // date-time fields so an overlap cannot be picked. This wave's own span is excluded: it is the
  // span being moved off, not an obstacle.
  const openResolvedNow = resolveAnchorLocally(anchorFromDraft(openDraft), {
    phaseByEvent: timeline.phaseByEvent,
    postFtMinutes: timeline.postFtMinutes
  });
  const slots = slotBounds(timeline, openResolvedNow?.at, {
    toLocal: toDatetimeLocal,
    ignoreWaveId: wave.waveId
  });

  const targetErr = target === '' ? null : validateTapTarget(target, bounds);
  const rewardsErr = validateRewards(rewards, effectiveTarget);

  const built = original
    ? buildAnchorUpdatePayload({
        wave,
        status,
        openDraft,
        closeDraft,
        original,
        target,
        rewards: normalizeRewards(rewards),
        baseRewards: null
      })
    : null;

  const blockingError =
    validation.open ||
    validation.close ||
    validation.pair ||
    orderErr ||
    targetErr ||
    rewardsErr ||
    built?.blocked ||
    null;

  const canSave = !terminal && !blockingError && !!built?.changed?.length && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setServerError(null);
    const res = await Api.put('/admin/fanwave/update', built.payload, null, { silent: true });
    setSaving(false);
    if (!res || res.err) {
      // A 409 means the wave is spent — the row's own status may simply be stale.
      if (res?.status === 409) {
        setServerError(
          `${res.err || 'This wave can no longer be edited.'} Delete it instead — an edit is refused once a wave has finished.`
        );
      } else {
        setServerError(res?.err || 'Could not save this wave. Please try again.');
      }
      return;
    }
    setSaved(true);
    // Every mutation refetches the timeline: a pending wave's position may have moved, and this
    // save may itself have changed which waves overlap or block each other.
    onSaved && onSaved(res);
  };

  const openDisabledReason = isLive
    ? 'This wave is running. Its start is fixed because every fan’s participation is anchored to it — cancel the wave and enable a new one to move it.'
    : null;

  const liveCloseKinds = isLive ? LIVE_CLOSE_KINDS : null;

  return (
    <Col
      noFlex
      wid="100%"
      style={{
        borderRadius: 12,
        border: `1px solid ${conflicts.length ? '#FCA5A5' : theme.GREY_20}`,
        background: '#fff',
        overflow: 'hidden'
      }}
    >
      <Row
        center
        between
        noFlex
        wid="100%"
        pad="11px 14px"
        gap="10px"
        onClick={onToggle}
        style={{ cursor: 'pointer', flexWrap: 'wrap' }}
      >
        <Row center noFlex gap="9px" style={{ minWidth: 0 }}>
          {expanded ? <FiChevronDown size={15} /> : <FiChevronRight size={15} />}
          <Text b2 bold marg="0" style={{ fontSize: 13 }}>
            {`${index + 1}.`}
          </Text>
          <Text b2 bold marg="0" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
            {waveEdgesLabel(anchors, wave, fmtDateTime)}
          </Text>
          {anchors && !anchors.exact && (!anchors.openExact || !anchors.closeExact) && (
            <Text
              c1
              col={theme.GREY_50}
              marg="0"
              title={
                'Shown as a time, not an anchor: the API does not return a wave’s anchors on read, and this edge’s stored schedule field is empty — so what it is anchored TO cannot be recovered, only when it resolves to. Open this wave from a fresh create or save to see the anchor itself.'
              }
              style={{ fontSize: 10.5, fontStyle: 'italic' }}
            >
              resolved time
            </Text>
          )}
        </Row>
        <Row center noFlex gap="8px" style={{ flexWrap: 'wrap' }}>
          <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
            {`${fmtMaybe(wave.opensAt, !wave.pending, fmtDateTime)} → ${fmtMaybe(
              wave.closesAt,
              !wave.pending,
              fmtDateTime
            )}`}
          </Text>
          {wave.pending && (
            <Badge variant="warning">projected</Badge>
          )}
          <Badge variant={STATUS_BADGE_VARIANT[status] || 'neutral'} dot>
            {status}
          </Badge>
        </Row>
      </Row>

      {expanded && (
        <Col noFlex wid="100%" pad="0 14px 14px" gap="13px">
          {wave.pending && (
            <Notice tone="info" title="This wave’s position will move">
              At least one edge is still projected from kick-off rather than observed. The times
              above are estimates and shift as the match progresses — that is the schedule working,
              not a glitch.
            </Notice>
          )}

          {conflicts.length > 0 && (
            <Col noFlex wid="100%" gap="8px">
              {conflicts.map((conflict, i) => (
                <ConflictNotice key={i} conflict={conflict} timeline={timeline} />
              ))}
            </Col>
          )}

          {terminal ? (
            <Notice tone="neutral" title="This wave has finished">
              An edit is refused with a 409 once a wave has completed, been cancelled or expired.
              Its schedule is history — delete it if you want it off the timeline.
            </Notice>
          ) : (
            <>
              <Col noFlex wid="100%" style={{ maxWidth: 420 }}>
                <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
                  Preset
                </Text>
                <Select
                  value={currentPreset?.key || presetKey || ''}
                  disabled={isLive}
                  onChange={(e) => applyPreset(e.target.value)}
                  aria-label="Schedule preset"
                >
                  <option value="">{currentPreset ? currentPreset.label : 'Custom'}</option>
                  {WAVE_PRESETS.filter((p) => p.group === 'core').map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                  {WAVE_PRESETS.filter((p) => p.group === 'extra').map((p) => (
                    <option key={p.key} value={p.key}>
                      {`· ${p.label}`}
                    </option>
                  ))}
                </Select>
                <FieldHint>
                  {presetByKey(presetKey)?.hint ||
                    currentPreset?.hint ||
                    'A preset only seeds the two pickers below — edit either edge afterwards, or drag its handle on the track.'}
                </FieldHint>
              </Col>

              <Row noFlex wid="100%" gap="12px" style={{ flexWrap: 'wrap', alignItems: 'stretch' }}>
                <Col noFlex style={{ flex: '1 1 280px', minWidth: 260 }}>
                  <AnchorPicker
                    edge={ANCHOR_EDGE.OPEN}
                    draft={openDraft}
                    onChange={setOpenDraft}
                    error={validation.open}
                    disabled={isLive}
                    disabledReason={openDisabledReason}
                    phaseByEvent={timeline.phaseByEvent}
                    postFtMinutes={timeline.postFtMinutes}
                    min={slots?.openMin}
                    max={slots?.openMax}
                  />
                </Col>
                <Col noFlex style={{ flex: '1 1 280px', minWidth: 260 }}>
                  <AnchorPicker
                    edge={ANCHOR_EDGE.CLOSE}
                    draft={closeDraft}
                    onChange={(next) =>
                      setCloseDraft(
                        // A live wave can only close on a date-time or a match minute, because
                        // those are the only kinds with a legacy field to travel on.
                        liveCloseKinds && !liveCloseKinds.includes(next.kind) ? closeDraft : next
                      )
                    }
                    error={validation.close}
                    phaseByEvent={timeline.phaseByEvent}
                    postFtMinutes={timeline.postFtMinutes}
                    openResolved={openResolvedNow}
                    min={slots?.closeMin}
                    max={slots?.closeMax}
                  />
                </Col>
              </Row>

              {validation.pair && <FieldError>{validation.pair}</FieldError>}
              {orderErr && <FieldError>{orderErr}</FieldError>}

              {isLive && (
                <Notice tone="warning" title="Running — start locked, close still editable">
                  A running wave’s start cannot change. Its close can, but only as a date & time or
                  a match minute: the API refuses an anchor pair on a live wave, so an event or
                  duration close is unreachable until the wave is cancelled and re-enabled.
                </Notice>
              )}

              <RewardsFields
                rewards={rewards}
                onChange={setRewards}
                target={target}
                effectiveTarget={effectiveTarget}
                onTargetChange={setTarget}
                targetError={targetErr}
                targetDescription={`Leave blank to inherit the channel default${
                  config?.tapTarget ? ` (${config.tapTarget})` : ''
                }.`}
                numbered={false}
              />

              {rewardsErr && <FieldError>{rewardsErr}</FieldError>}
              {built?.blocked && <FieldError>{built.blocked}</FieldError>}
              {serverError && <Notice tone="danger" title="Could not save">{serverError}</Notice>}
              {saved && !serverError && (
                <Notice tone="info" title="Saved">
                  The timeline has been refetched — positions of projected waves may have moved.
                </Notice>
              )}

              {built?.targetChanged && isLive && (
                <Notice tone="warning" title="Changing a live wave’s target">
                  Fans are already tapping toward the current target. Saving sends the change with
                  an explicit confirmation and connected fans see it immediately.
                </Notice>
              )}
            </>
          )}

          <Row center noFlex wid="100%" gap="9px" style={{ flexWrap: 'wrap' }}>
            {!terminal && (
              <Button
                text={saving ? 'Saving…' : 'Save wave'}
                onClick={save}
                disabled={!canSave}
                bg={theme.SECONDARY}
                small
                wid="130px"
              />
            )}
            {!terminal && !isLive && onRequestCancel && (
              <Button
                text="Cancel wave"
                onClick={() => onRequestCancel(wave)}
                bordered
                small
                wid="130px"
              />
            )}
            {terminal && onRequestDelete && (
              <Button
                text="Delete wave"
                onClick={() => onRequestDelete(wave)}
                bg={theme.SECONDARY}
                small
                wid="130px"
              />
            )}
            {!terminal && !blockingError && !built?.changed?.length && (
              <Text c1 col={theme.GREY_50} marg="0" style={{ fontSize: 11.5 }}>
                Nothing changed yet.
              </Text>
            )}
          </Row>
        </Col>
      )}
    </Col>
  );
};

const ConflictNotice = ({ conflict, timeline }) => {
  const copy = describeConflict(conflict, timeline, fmtDateTime);
  return (
    <Notice tone={copy.tone} title={copy.title}>
      {copy.detail}
    </Notice>
  );
};

export default WaveEditorRow;
